import React, { useEffect, useMemo, useState } from "react"
import { useStore } from "../../../stores/RootStoreContext"
import { IClientRequestDto } from "../../../types/request/IClientRequestDto"
import { CommonInput } from "./fields/CommonInput"
import { IErrorModel } from "../../../types/error/IErrorModel"
import { CountryPicker } from "./fields/CountryPicker"
import { ICountry } from "../../../types/dict/ICountry"
import { CurrencyPicker } from "./fields/CurrencyPicker"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { round2Digits, spacedNum } from "../CommonHelper"
import { IInnRegistryDto } from "../../../types/inn/IInnRegistryDto"
import { IClientRequestStatusDto } from "../../../types/request/IClientRequestStatusDto"
import { IClientContractorDto } from "../../../types/contractor/IClientContractorDto"
import { ICompanyBank, IFullCompanyDto } from "@/types/company/IFullCompanyDto"
import { ModalPopUp } from "./ModalPopUp"
import { IModalMessage } from "@/types/error/IModalMessage"

const APPROVED = "APPROVED"

export const CurrencyRequestPopUp: React.FC<{
  visible: boolean
  setVisible: (visible: boolean) => void
  id?: number | undefined
}> = ({ visible, setVisible, id }) => {
  const { userStore } = useStore()

  const [companies, setCompanies] = useState<IFullCompanyDto[]>([])
  const [company, setCompany] = useState<IFullCompanyDto>()
  const [bank, setBank] = useState<ICompanyBank>()
  const [contractors, setContractors] = useState<IClientContractorDto[]>([])
  const [clientRequest, setClientRequest] = useState<IClientRequestDto | null>(null)
  const [innRegistry, setInnRegistry] = useState<IInnRegistryDto>()
  const [comment, setComment] = useState<string | undefined>()
  const [errors, setErrors] = useState<IErrorModel>()
  const [sending, setSending] = useState(false)
  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)

  const approvedContactors = useMemo(() => {
    if (!contractors.length) {
      return []
    }

    return contractors.filter(el => el.status === APPROVED)
  }, [contractors])

  const handleClosePopUp = () => {
    setVisible(false)
    setClientRequest(null)
    setInnRegistry(undefined)
    setComment(undefined)
    setErrors(undefined)
  }

  useEffect(() => {
    if (id && visible) {
      TrinityAPI.getClientCurrencyRequest(id, response => {
        if (response.status === 200) {
          setClientRequest(response.data)
          setComment(response.data.comment)
        } else {
          setErrors(errors)
        }
      })
    }
  }, [id, visible])

  useEffect(() => {
    TrinityAPI.getClientCurrencyInnList(0, "", response => {
      setContractors(response.data.items)
    })
  }, [])

  useEffect(() => {
    if (clientRequest && clientRequest.inn && (userStore.isManager || userStore.isAdmin)) {
      TrinityAPI.getInnRegistryByInn(clientRequest.inn, response => {
        if (response.status === 200) {
          setInnRegistry(response.data)
        }
      })
    }
  }, [clientRequest])

  const changeRecipientBankCountry = (countryList: ICountry[]) => {
    if (countryList.length === 0) {
      setClientRequest({ ...clientRequest, currencyPayerBankCountry: undefined })
    } else {
      setClientRequest({ ...clientRequest, currencyPayerBankCountry: countryList[0] })
    }
  }

  const handleContractorSelectChange = (id: string) => {
    if (id) {
      const pickedContractor = contractors.filter(c => Number(c.id) === Number(id))[0]
      if (pickedContractor) {
        setClientRequest({
          ...clientRequest,
          currencyPayerId: Number(pickedContractor.id),
          currencyPayerBankCountry: pickedContractor.countryRef
        })
      }
    }
  }

  const submit = () => {
    if (!clientRequest || sending) return

    setSending(true)
    const request = {
      currencyPayerId: clientRequest.currencyPayerId,
      currencyPayerBankCountry: clientRequest.currencyPayerBankCountry,
      invoicePrice: round2Digits(Number(clientRequest.invoicePrice?.replaceAll(" ", "")) * 100).toString(),
      currency: clientRequest.currency
    }

    TrinityAPI.createClientCurrencyRequest(request, response => {
      if (response.status === 200) {
        window.location.href = "/request?status=NEW&type=currency"
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const approveReject = (status: string) => {
    if (!clientRequest || !clientRequest.id || sending) return

    const isRequestApproved = status === "APPROVED"
    const { companyId, companyBankId } = clientRequest

    if (isRequestApproved && (!companyBankId || !companyId)) return

    setSending(true)
    const request: IClientRequestStatusDto = {
      status: status,
      comment: comment,
      ...(!!companyId && {
        companyId: Number(companyId)
      }),
      ...(!!companyBankId && {
        companyBankId: Number(companyBankId)
      })
    }

    TrinityAPI.changeCurrencyInnRegistryItemStatus(clientRequest.id, request, response => {
      if (response.status === 200) {
        window.location.href = "/request?type=currency"
      } else {
        setErrors(response.data)
        setSending(false)
      }
    })
  }

  const blockUnblock = () => {
    const request: IInnRegistryDto = {
      comment: clientRequest?.comment,
      status: clientRequest?.status === "BLOCKED" ? "APPROVED" : "BLOCKED"
    }
    TrinityAPI.createInnRegistryItem(request, response => {
      if (response.status === 200) {
        window.location.reload()
      }
    })
  }

  const changeCompany = (companyId: string) => {
    setClientRequest({
      ...clientRequest,
      companyId,
      companyBankId: null
    })
    setCompany(companies.find(company => String(company.id) === companyId))
  }

  const changeCompanyBank = (companyBankId: string) => {
    console.log(companyBankId)
    setClientRequest({
      ...clientRequest,
      companyBankId
    })
    setBank(company?.banks?.find(bank => String(bank.id) === companyBankId))
  }

  const getHumanStatus = (status: string | undefined) => {
    switch (status) {
      case "APPROVED":
        return "подтвержден"
      case "REJECTED":
        return "отклонен"
      case "USED":
        return "с пополнением"
      default:
        return "новый"
    }
  }

  useEffect(() => {
    TrinityAPI.getCompanyList(response => {
      if (response.status === 200) {
        setCompanies(response.data)
      } else {
        setModalMessage({
          title: "Ошибка!",
          text: `Ошибка загрузки списка компаний: ${response.data}`
        })
      }
    })
  }, [])

  useEffect(() => {
    if (clientRequest?.companyId) {
      setCompany(companies.find(company => String(company.id) === String(clientRequest?.companyId)))
    }
  }, [companies])

  useEffect(() => {
    if (clientRequest?.companyBankId) {
      setBank(company?.banks?.find(bank => String(bank.id) === String(clientRequest?.companyBankId)))
    }
  }, [company])

  if (visible) {
    return (
      <>
        <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
        <section className="popup-forms index-top" id="request_open">
          <div className="container-popup-forms">
            <button className="close-btn-card" id="request_close" onClick={handleClosePopUp} />
            <div className="popup-card">
              <h4 className="heading-chapter">
                {clientRequest?.id ? (
                  `Валютный запрос №${clientRequest.id}: ${getHumanStatus(clientRequest.status)}`
                ) : (
                  <>Новый валютный запрос</>
                )}
              </h4>
              {userStore.isAdmin || userStore.isManager ? (
                <>
                  <fieldset>
                    <legend>Название компании плательщика</legend>
                    <p className="text-form">{clientRequest?.currencyPayerName}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Юрисдикция банка получателя</legend>
                    <p className="text-form">{clientRequest?.currencyPayerBankCountry?.name?.toUpperCase()}</p>
                  </fieldset>
                  <fieldset>
                    <legend>Сумма</legend>
                    <p className="text-form">
                      {spacedNum(round2Digits(Number(clientRequest?.invoicePrice) / 100))} {clientRequest?.currency}
                    </p>
                  </fieldset>
                  <CommonInput
                    typeName={"text"}
                    label={"Комментарий"}
                    placeholder={"Введите причину отказа для клиента"}
                    name={"comment"}
                    value={comment}
                    errorModel={errors}
                    _callback={e => setComment(e)}
                  />
                  <div className="flex-column-16">
                    <fieldset>
                      <legend>Выберите зарубежную компанию</legend>
                      <select
                        value={String(clientRequest?.companyId)}
                        onChange={e => changeCompany(e.target.value)}
                        className="text-form"
                      >
                        <option value="">Выберите компанию</option>
                        {companies.map((c, index) => (
                          <option value={c.id} key={index}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </fieldset>

                    <fieldset style={!company ? { pointerEvents: "none", opacity: "0.5" } : {}}>
                      <legend>Выберите счет</legend>
                      <select
                        value={String(clientRequest?.companyBankId)}
                        onChange={e => changeCompanyBank(e.target.value)}
                        className="text-form"
                      >
                        <option value="">Выберите счет</option>
                        {(company?.banks?.length || 0) > 0
                          ? company?.banks?.map((c, index) => (
                              <option value={String(c.id)} key={index}>
                                #{c.id} {c.swift} - {c.accountNumber} - {c.currency}
                              </option>
                            ))
                          : null}
                      </select>
                    </fieldset>
                  </div>
                  <div className="flex-row-justify">
                    <div className="flex-row-24">
                      {clientRequest && clientRequest.status === "APPROVED" ? (
                        <></>
                      ) : (
                        <button className="btn" onClick={() => approveReject("APPROVED")}>
                          Подтвердить
                        </button>
                      )}
                      {clientRequest && clientRequest.status === "REJECTED" ? (
                        <></>
                      ) : (
                        <button className="btn grey-btn" onClick={() => approveReject("REJECTED")}>
                          Отклонить запрос
                        </button>
                      )}
                    </div>
                    {/* <button className="btn black-btn" onClick={blockUnblock}>
                      {innRegistry && innRegistry.status === "BLOCKED" ? "Разблокировать ИНН" : "Заблокировать ИНН"}
                    </button> */}
                  </div>
                </>
              ) : (
                <>
                  <fieldset>
                    <legend>Выберите плательщика</legend>
                    <select onChange={e => handleContractorSelectChange(e.target.value)} className="input-form">
                      <option value="">Выберите плательщика</option>
                      {approvedContactors?.map((i, index) => (
                        <option value={i.id} key={index}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </fieldset>
                  <CountryPicker
                    label={"Юрисдикция банка получателя"}
                    size={1}
                    name={"currencyPayerBankCountry"}
                    value={
                      clientRequest?.currencyPayerBankCountry ? new Array(clientRequest.currencyPayerBankCountry) : []
                    }
                    errorModel={errors}
                    _callback={changeRecipientBankCountry}
                  />
                  <CommonInput
                    typeName={"number"}
                    label={"Сумма"}
                    placeholder={"В валюте получателя"}
                    name={"invoicePrice"}
                    value={clientRequest?.invoicePrice}
                    errorModel={errors}
                    _callback={e => setClientRequest({ ...clientRequest, invoicePrice: e })}
                  />
                  <CurrencyPicker
                    label={"Валюта"}
                    name={"currency"}
                    errorModel={errors}
                    initValue={clientRequest?.currency}
                    _callback={e => setClientRequest({ ...clientRequest, currency: e })}
                  />
                  <div className="flex-row-24">
                    <button className="btn" onClick={submit}>
                      {sending ? "Подождите..." : "Создать"}
                    </button>
                    <button className="btn grey-btn" onClick={handleClosePopUp}>
                      Отменить
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </>
    )
  } else {
    return <></>
  }
}
